// src/features/tasks/components/TaskAssignmentsSection.jsx
import React, { memo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  Autocomplete,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Person as PersonIcon,
  PublicOutlined as PublicIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProfilePicture from '../../../../components/common/ProfilePicture';
import { FormSection } from '../../../../components/common/form';

const WEEKDAYS = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

const WeekdayAssignmentBox = memo(function WeekdayAssignmentBox({
  weekday,
  assignment,
  staffMembers,
  onAssignmentChange,
  loading,
}) {
  const { t } = useTranslation(['common', 'tasks']);

  const selectedPrimary = staffMembers.find(s => s.staff_member_id === assignment?.primary_id) || null;
  const selectedSecondary = staffMembers.find(s => s.staff_member_id === assignment?.secondary_id) || null;

  const handlePrimaryChange = useCallback((event, value) => {
    onAssignmentChange(weekday, {
      ...assignment,
      primary_id: value?.staff_member_id || null,
    });
  }, [weekday, assignment, onAssignmentChange]);

  const handleSecondaryChange = useCallback((event, value) => {
    onAssignmentChange(weekday, {
      ...assignment,
      secondary_id: value?.staff_member_id || null,
    });
  }, [weekday, assignment, onAssignmentChange]);

  const weekdayLabel = t(`common:rrule.weekdays.${weekday}`);

  return (
    <Card variant="outlined" sx={{ p: 4, height: '100%' }}>
      <Typography variant="subtitle2" fontWeight="bold" mb={2} textTransform="capitalize">
        {weekdayLabel}
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        <Autocomplete
          size="small"
          options={staffMembers}
          getOptionLabel={(option) => option.name}
          value={selectedPrimary}
          onChange={handlePrimaryChange}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('tasks:labels.primary')}
              placeholder={t('tasks:form.assignments.select_primary')}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedPrimary ? (
                  <InputAdornment position="start">
                    <ProfilePicture
                      profilePictureFileId={selectedPrimary?.profile_picture_file_id}
                      name={selectedPrimary?.name}
                      size={20}
                      imageSize="32x32"
                      bgcolor="grey.400"
                      color={selectedPrimary?.color}
                    />
                  </InputAdornment>
                ) : params.InputProps.startAdornment,
              }}
            />
          )}
          renderOption={({ key, ...props }, option) => (
            <Box key={key} component="li" {...props}>
              <ProfilePicture
                profilePictureFileId={option?.profile_picture_file_id}
                name={option?.name}
                size={32}
                imageSize="64x64"
                bgcolor="grey.400"
                color={option?.color}
                sx={{ mr: 2 }}
              />
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          )}
        />
        <Autocomplete
          size="small"
          options={staffMembers.filter(member => member.staff_member_id !== assignment?.primary_id)}
          getOptionLabel={(option) => option.name}
          value={selectedSecondary}
          onChange={handleSecondaryChange}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('tasks:labels.secondary')}
              placeholder={t('tasks:form.assignments.select_secondary')}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedSecondary ? (
                  <InputAdornment position="start">
                    <ProfilePicture
                      profilePictureFileId={selectedSecondary?.profile_picture_file_id}
                      name={selectedSecondary?.name}
                      size={20}
                      imageSize="32x32"
                      bgcolor="grey.400"
                      color={selectedSecondary?.color}
                    />
                  </InputAdornment>
                ) : params.InputProps.startAdornment,
              }}
            />
          )}
          renderOption={({ key, ...props }, option) => (
            <Box key={key} component="li" {...props}>
              <ProfilePicture
                profilePictureFileId={option?.profile_picture_file_id}
                name={option?.name}
                size={32}
                imageSize="64x64"
                bgcolor="grey.400"
                color={option?.color}
                sx={{ mr: 2 }}
              />
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          )}
        />
      </Box>
    </Card>
  );
});

const TaskAssignmentsSection = memo(function TaskAssignmentsSection({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
  number,
  staffMembers = [],
}) {
  const { t } = useTranslation(['common', 'tasks']);

  const assignmentMode = formData.assignment_mode || 'simple';

  const handleAssignmentModeChange = useCallback((event) => {
    const newMode = event.target.value;
    onInputChange('assignment_mode', newMode);

    // Clear relevant fields based on mode
    if (newMode === 'all') {
      onInputChange('primary_assignee_id', null);
      onInputChange('secondary_assignee_id', null);
      onInputChange('weekday_assignments', null);
    } else if (newMode === 'simple') {
      onInputChange('weekday_assignments', null);
    } else if (newMode === 'weekday') {
      onInputChange('primary_assignee_id', null);
      onInputChange('secondary_assignee_id', null);
      // Initialize weekday assignments if not present
      if (!formData.weekday_assignments) {
        const initialWeekdayAssignments = {};
        WEEKDAYS.forEach(day => {
          initialWeekdayAssignments[day] = { primary_id: null, secondary_id: null };
        });
        onInputChange('weekday_assignments', initialWeekdayAssignments);
      }
    }
  }, [formData.weekday_assignments, onInputChange]);

  const handleAutocompleteChange = useCallback((field) => (event, value) => {
    onInputChange(field, value?.staff_member_id || null);
  }, [onInputChange]);

  const handleWeekdayAssignmentChange = useCallback((weekday, assignment) => {
    const updatedAssignments = {
      ...(formData.weekday_assignments || {}),
      [weekday]: assignment,
    };
    onInputChange('weekday_assignments', updatedAssignments);
  }, [formData.weekday_assignments, onInputChange]);

  // Find selected staff members for autocomplete (simple mode)
  const selectedPrimaryAssignee = staffMembers.find(s => s.staff_member_id === formData.primary_assignee_id) || null;
  const selectedSecondaryAssignee = staffMembers.find(s => s.staff_member_id === formData.secondary_assignee_id) || null;

  return (
    <FormSection
      number={number}
      title={t('tasks:form.sections.assignees')}
    >

        {/* Assignment Mode Selection */}
        <Box mb={4}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            {t('tasks:form.assignments.mode_label')}
          </Typography>
          <RadioGroup
            value={assignmentMode}
            onChange={handleAssignmentModeChange}
            row
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <PublicIcon fontSize="small" />
                  <Typography variant="body2">{t('tasks:form.assignments.mode_all')}</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="simple"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon fontSize="small" />
                  <Typography variant="body2">{t('tasks:form.assignments.mode_direct')}</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="weekday"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarIcon fontSize="small" />
                  <Typography variant="body2">{t('tasks:form.assignments.mode_weekly')}</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        {/* Assignment Content Based on Mode */}
        {assignmentMode === 'all' && (
          <Alert severity="info" icon={<PublicIcon />}>
            <AlertTitle>{t('tasks:form.assignments.all_mode_title')}</AlertTitle>
            {t('tasks:form.assignments.all_mode_description')}
          </Alert>
        )}

        {assignmentMode === 'simple' && (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Assignee Fields Row */}
            <Box
              display="flex"
              flexWrap="wrap"
              gap={3}
              sx={{
                '& > *': {
                  flex: '1 1 300px',
                  minWidth: '300px',
                }
              }}
            >
              <Autocomplete
                options={staffMembers}
                getOptionLabel={(option) => option.name}
                value={selectedPrimaryAssignee}
                onChange={handleAutocompleteChange('primary_assignee_id')}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('tasks:labels.primary_assignee')}
                    placeholder={t('tasks:form.assignments.primary_placeholder')}
                    error={!!validationErrors.primary_assignee_id}
                    helperText={validationErrors.primary_assignee_id || t('tasks:form.assignments.primary_helper')}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            {selectedPrimaryAssignee ? (
                              <ProfilePicture
                                profilePictureFileId={selectedPrimaryAssignee?.profile_picture_file_id}
                                name={selectedPrimaryAssignee?.name}
                                size={24}
                                imageSize="32x32"
                                bgcolor="grey.400"
                                color={selectedPrimaryAssignee?.color}
                              />
                            ) : (
                              <PersonIcon color="primary" />
                            )}
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={({ key, ...props }, option) => (
                  <Box key={key} component="li" {...props}>
                    <ProfilePicture
                      profilePictureFileId={option?.profile_picture_file_id}
                      name={option?.name}
                      size={36}
                      imageSize="64x64"
                      bgcolor="grey.400"
                      color={option?.color}
                      sx={{ mr: 2 }}
                    />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('common:labels.id')} {option.staff_member_id.substring(0, 8)}...
                      </Typography>
                    </Box>
                  </Box>
                )}
              />

              <Autocomplete
                options={staffMembers.filter(member => member.staff_member_id !== formData.primary_assignee_id)}
                getOptionLabel={(option) => option.name}
                value={selectedSecondaryAssignee}
                onChange={handleAutocompleteChange('secondary_assignee_id')}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('tasks:labels.secondary_assignee')}
                    placeholder={t('tasks:form.assignments.secondary_placeholder')}
                    error={!!validationErrors.secondary_assignee_id}
                    helperText={validationErrors.secondary_assignee_id || t('tasks:form.assignments.secondary_helper')}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            {selectedSecondaryAssignee ? (
                              <ProfilePicture
                                profilePictureFileId={selectedSecondaryAssignee?.profile_picture_file_id}
                                name={selectedSecondaryAssignee?.name}
                                size={24}
                                imageSize="32x32"
                                bgcolor="grey.400"
                                color={selectedSecondaryAssignee?.color}
                              />
                            ) : (
                              <PersonIcon color="primary" />
                            )}
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={({ key, ...props }, option) => (
                  <Box key={key} component="li" {...props}>
                    <ProfilePicture
                      profilePictureFileId={option?.profile_picture_file_id}
                      name={option?.name}
                      size={36}
                      imageSize="64x64"
                      bgcolor="grey.400"
                      color={option?.color}
                      sx={{ mr: 2 }}
                    />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('common:labels.id')} {option.staff_member_id.substring(0, 8)}...
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>
          </Box>
        )}

        {assignmentMode === 'weekday' && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t('tasks:form.assignments.weekly_mode_description')}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={4}>
              {WEEKDAYS.map((weekday) => (
                <Box
                  key={weekday}
                  // Fixed columns (4 / 2 / 1) so the last row doesn't stretch wider than the rest; gap={4} = 1rem
                  sx={{ flex: '0 0 auto', minWidth: 0, width: { xs: '100%', sm: 'calc((100% - 1rem) / 2)', md: 'calc((100% - 3rem) / 4)' } }}
                >
                  <WeekdayAssignmentBox
                    weekday={weekday}
                    assignment={formData.weekday_assignments?.[weekday] || { primary_id: null, secondary_id: null }}
                    staffMembers={staffMembers}
                    onAssignmentChange={handleWeekdayAssignmentChange}
                    loading={loading}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
    </FormSection>
  );
});

export default TaskAssignmentsSection;
